import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Users, Layers, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, Badge } from "../components/kit";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { CATALYST_TIERS, fmtLimit } from "../lib/plans";
import { ContactCustomModal } from "../components/ContactCustomModal";

const VITALS_POINTS = [
  "The 25-question baseline and your Business Health Score",
  "The daily three-question check-in",
  "Streaks, badges and your score history",
  "Anonymous benchmarking against a peer cohort",
  "Weekly progress reports by email",
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState(null);
  const [contact, setContact] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const startVitals = () => navigate(!user ? "/register" : user.role === "owner" ? (user.onboarding_complete ? "/dashboard" : "/onboarding") : "/register");

  const chooseTier = async (t) => {
    if (t.custom) { setContact(true); return; }
    if (!user) { navigate("/register?role=coach"); return; }
    if (user.role !== "coach" || user.team_member) {
      toast.info(user.team_member ? "Only your account owner can change the plan." : "Catalyst plans are for coaches — create a coach account to choose one.");
      return;
    }
    if (t.monthly === 0) { navigate("/coach/settings#plan"); return; }
    setBusy(t.id);
    try {
      const { data } = await api.post("/coach/plan/change", { tier: t.id, interval: annual ? "year" : "month", origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(data.status === "scheduled" ? "Change scheduled for the end of your billing period" : "Plan updated");
      navigate("/coach/settings#plan");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="pricing-page">
      <section className="buktu-wrap" style={{ paddingTop: "var(--space-20)", paddingBottom: "var(--space-10)", textAlign: "center" }}>
        <div className="buktu-rise" style={{ maxWidth: 680, margin: "0 auto" }}>
          <span className="buktu-eyebrow">Pricing</span>
          <h1 style={{ font: "var(--type-display)", fontSize: "clamp(32px, 5vw, 49px)", letterSpacing: "var(--tracking-tight)", margin: "var(--space-4) 0 var(--space-4)" }}>
            Free for business owners. Simple for coaches.
          </h1>
          <p style={{ font: "var(--type-body)", fontSize: 18, color: "var(--text-muted)" }}>
            Business owners use Vitals at no cost. Coaches and organizations pay for Catalyst based on how many active business owners they serve.
          </p>
        </div>
      </section>

      {/* Section 1 — business owners */}
      <section className="buktu-wrap" style={{ paddingBottom: "var(--space-16)" }} data-brand="vitals">
        <Card padding="0" data-testid="pricing-vitals" style={{ overflow: "hidden", boxShadow: "var(--shadow-lg)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr" }} className="buktu-vitals-grid">
            <div style={{ background: "var(--buktu-green-dark)", padding: "var(--space-10)", display: "flex", flexDirection: "column", gap: "var(--space-4)", justifyContent: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                <img src="/brand/vitals-mark.svg" alt="" style={{ width: 36, height: 36 }} />
                <span style={{ font: "var(--type-label)", color: "var(--buktu-sage)" }}>For business owners</span>
              </div>
              <h2 data-testid="pricing-vitals-headline" style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "clamp(34px, 5vw, 52px)", letterSpacing: "var(--tracking-tight)", color: "#fff", lineHeight: 1.05 }}>Vitals is free.</h2>
              <p style={{ font: "var(--type-body)", color: "rgba(255,255,255,0.75)" }}>Vitals Core, for every business owner. No trial, no card, no payment step — and your access never depends on a coach's plan.</p>
              <div><Button size="lg" data-testid="pricing-vitals-cta" onClick={startVitals} iconRight={<ArrowRight size={18} />}>Get Started</Button></div>
            </div>
            <div style={{ padding: "var(--space-10)", display: "flex", flexDirection: "column", justifyContent: "center", gap: "var(--space-3)" }}>
              <span className="buktu-eyebrow">Everything in Vitals Core</span>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                {VITALS_POINTS.map((f) => <Li key={f}>{f}</Li>)}
              </ul>
            </div>
          </div>
        </Card>
      </section>

      {/* Section 2 — coaches & organizations */}
      <section className="buktu-wrap" style={{ paddingBottom: "var(--space-10)" }} data-brand="catalyst">
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "var(--space-5)", flexWrap: "wrap", marginBottom: "var(--space-8)" }}>
          <div style={{ maxWidth: 560 }}>
            <span className="buktu-eyebrow">For coaches & organizations</span>
            <h2 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)", marginTop: "var(--space-3)" }}>Catalyst, priced by active members</h2>
            <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginTop: 6 }}>An active member is a business owner who completed a check-in or onboarding step in the last 30 days. Dormant sign-ups don't count, and someone in two of your cohorts counts once.</p>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: 6, borderRadius: "var(--radius-pill)", background: "var(--surface-card)", border: "1px solid var(--border-hairline)", boxShadow: "var(--shadow-xs)" }}>
            <button data-testid="billing-monthly-btn" onClick={() => setAnnual(false)} style={billBtn(!annual)}>Monthly</button>
            <button data-testid="billing-annual-btn" onClick={() => setAnnual(true)} style={{ ...billBtn(annual), display: "inline-flex", alignItems: "center", gap: 8 }}>
              Annual
              <span style={{ font: "var(--type-eyebrow)", fontWeight: 700, color: annual ? "#fff" : "var(--buktu-green)", background: annual ? "rgba(255,255,255,0.2)" : "var(--surface-brand-wash)", padding: "2px 8px", borderRadius: 999 }}>2 months free</span>
            </button>
          </div>
        </div>

        <div className="buktu-price-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "var(--space-4)", alignItems: "stretch" }}>
          {CATALYST_TIERS.map((t) => <TierCard key={t.id} t={t} annual={annual} busy={busy === t.id} onSelect={() => chooseTier(t)} />)}
        </div>
        <p style={{ textAlign: "center", font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: "var(--space-8)" }}>
          Annual plans are billed once a year — 2 months free. Prices in USD. Going over your member limit? You get a two-month grace period, and business owners are never removed or locked out.
        </p>
      </section>

      <ContactCustomModal open={contact} onClose={() => setContact(false)} />

      <style>{`
        @media (max-width: 1180px) { .buktu-price-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
        @media (max-width: 760px) { .buktu-price-grid, .buktu-vitals-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

function TierCard({ t, annual, busy, onSelect }) {
  const price = annual ? t.annual : t.monthly;
  return (
    <Card padding="var(--space-6)" data-testid={`pricing-tier-${t.id}`} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", position: "relative", border: t.highlight ? "2px solid var(--buktu-green)" : "1px solid var(--border-hairline)", boxShadow: t.highlight ? "var(--shadow-lg)" : "var(--shadow-sm)" }}>
      {t.highlight && <div style={{ position: "absolute", top: -12, left: "var(--space-6)" }}><Badge tone="accent" dot>Most popular</Badge></div>}
      <span style={{ font: "var(--type-heading)", fontSize: 17, color: "var(--text-strong)", whiteSpace: "nowrap" }}>{t.name}</span>
      <div style={{ minHeight: 64 }}>
        {t.custom ? (
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 32, color: "var(--text-strong)" }}>Custom</span>
        ) : (
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span data-testid={`pricing-price-${t.id}`} style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 38, letterSpacing: "var(--tracking-tight)", color: "var(--text-strong)" }}>${price.toLocaleString()}</span>
            {t.monthly > 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>{annual ? "/yr" : "/mo"}</span>}
          </div>
        )}
        {annual && t.monthly > 0 && <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--buktu-green-deep)" }}>2 months free</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)", font: "var(--type-body-sm)", fontSize: 13 }}>
        <Lim icon={<Users size={13} />} text={t.custom ? "2,500+ active members" : `Up to ${fmtLimit(t.members)} active members`} />
        <Lim icon={<Layers size={13} />} text={t.custom ? "Custom cohorts" : `${t.cohorts} cohort${t.cohorts > 1 ? "s" : ""}`} />
        <Lim icon={<UserPlus size={13} />} text={t.custom ? "Custom team size" : `${t.seats} coaching seat${t.seats > 1 ? "s" : ""}, including you`} />
      </div>
      <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>{t.blurb}</p>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-2)", flex: 1 }}>
        {t.features.map((f) => <Li key={f} small>{f}</Li>)}
      </ul>
      <Button full variant={t.highlight ? "primary" : "secondary"} data-testid={`pricing-cta-${t.id}`} onClick={onSelect} disabled={busy} iconRight={<ArrowRight size={16} />}>
        {busy ? "Working…" : t.custom ? "Contact us" : t.monthly === 0 ? "Start free" : "Choose plan"}
      </Button>
    </Card>
  );
}

function Lim({ icon, text }) {
  return <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--buktu-green-deep)" }}>{icon}{text}</span>;
}

function Li({ children, small }) {
  return (
    <li style={{ display: "flex", gap: "var(--space-3)", font: "var(--type-body-sm)", fontSize: small ? 13 : undefined, color: "var(--text-body)" }}>
      <span style={{ width: 20, height: 20, borderRadius: 999, flexShrink: 0, background: "var(--surface-brand-wash)", color: "var(--buktu-green)", display: "grid", placeItems: "center", marginTop: 1 }}><Check size={12} /></span>
      {children}
    </li>
  );
}

const billBtn = (active) => ({
  border: 0, cursor: "pointer", padding: "8px 18px", borderRadius: "var(--radius-pill)", font: "var(--type-label)", fontWeight: 600,
  background: active ? "var(--buktu-green)" : "transparent", color: active ? "#fff" : "var(--text-muted)", transition: "var(--transition-control)",
});
